// Quick view — opens the shared <dialog> rendered by sections/quick-view.liquid (in the
// overlay group, so it is on every page) and fills it with that same section rendered at
// the PRODUCT's url through the Section Rendering API. Rendering it there is what gives
// the merchant's blocks a real `closest.product`: `content_for 'blocks'` cannot be handed
// one, so the URL supplies the context.
//
// Everything inside the dialog is theme blocks, so there is nothing here about prices,
// variants or money formatting: a variant change re-fetches the same url with &variant=
// (the product page's own mechanism) and add-to-cart is the buy-buttons form, which
// cart-drawer.js intercepts. Zero dependencies, deferred.
(() => {
  if (window.__quickView) return;
  window.__quickView = true;

  const dlg = document.querySelector('[data-quick-view-dialog]');
  if (!dlg || typeof dlg.showModal !== 'function') return;
  const body = dlg.querySelector('[data-qv-body]');
  const sectionId = dlg.dataset.qvSection;
  if (!body || !sectionId) return;

  const cache = new Map();

  const sectionUrl = (productUrl, variantId) => {
    const u = new URL(productUrl, location.origin);
    u.searchParams.set('section_id', sectionId);
    if (variantId) u.searchParams.set('variant', variantId);
    return u.pathname + u.search;
  };

  const load = async (url) => {
    if (cache.has(url)) return cache.get(url);
    const res = await fetch(url);
    if (!res.ok) throw new Error(res.status);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const next = doc.querySelector('[data-qv-body]');
    if (!next) throw new Error('no body');
    cache.set(url, next.innerHTML);
    return cache.get(url);
  };

  const fill = async (url) => {
    dlg.classList.add('is-loading');
    try {
      body.innerHTML = await load(url);
      return true;
    } catch {
      return false;
    } finally {
      dlg.classList.remove('is-loading');
    }
  };

  const triggerUrl = (btn) => btn.dataset.quickViewUrl || '/products/' + btn.dataset.quickView;

  const openFor = async (btn) => {
    const url = triggerUrl(btn);
    // Never flash the product that was open last while the next one is on the wire. A warm
    // cache swaps in the same frame, so only a real fetch empties the dialog (the body has
    // a min-height while empty so it does not open as a sliver and grow).
    if (dlg.dataset.qvUrl !== url && !cache.has(sectionUrl(url))) body.innerHTML = '';
    dlg.dataset.qvUrl = url;
    // The card's collection tint travels with the trigger, so the dialog picks up the same
    // family hue the card was painted in.
    if (btn.dataset.quickViewHue) dlg.style.setProperty('--collection-hue', btn.dataset.quickViewHue);
    else dlg.style.removeProperty('--collection-hue');

    dlg.showModal();
    if (!(await fill(sectionUrl(url)))) {
      dlg.close();
      location.href = url; // graceful fallback: the product page itself
    }
  };

  // The fetch is the only slow part of opening, so warm it while the pointer is still on
  // the card — by click time the render is usually already in the cache. Failures are
  // ignored; the click repeats the request and falls back to the product page.
  const prefetch = (e) => {
    const btn = e.target.closest && e.target.closest('[data-quick-view]');
    if (btn) load(sectionUrl(triggerUrl(btn))).catch(() => {});
  };
  document.addEventListener('pointerover', prefetch, { passive: true });
  document.addEventListener('focusin', prefetch);

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quick-view]');
    if (!btn) return;
    e.preventDefault();
    openFor(btn);
  });

  dlg.addEventListener('click', (e) => {
    // The dialog element itself is the backdrop; its content is [data-qv-body].
    if (e.target === dlg || e.target.closest('[data-qv-close]')) dlg.close();
  });

  // Variant change: re-render at the same url with the variant applied, exactly as the
  // product page does. Covers both picker styles — a <select name="id"> and swatch radios.
  dlg.addEventListener('change', (e) => {
    const input = e.target.closest('[name="id"]');
    if (!input || !dlg.dataset.qvUrl) return;
    fill(sectionUrl(dlg.dataset.qvUrl, input.value));
  });

  // cart-drawer.js's document-level submit handler does the add and opens the drawer; the
  // quick view just gets out of the way.
  dlg.addEventListener('submit', (e) => {
    if (e.target.closest('form[action$="/cart/add"]')) dlg.close();
  });
})();

